﻿import { IForm } from './interface/form';
import { CreateEditorFn } from './types/create-editor-fn';
import { IEditor } from './interface/editor';

import { isEmptyOrWhiteSpace } from '../utils/string';

import { TForm } from './components/t-form';
import { CalendarEditorTagName, createCalendarEditor } from './components/t-calendar-editor';
import { CheckEditorTagName, createCheckEditor } from './components/t-check-editor';
import { DatePickerEditorTagName, createDatePickerEditor } from './components/t-date-picker-editor';
import { SelectEditorTagName, createSelectEditor } from './components/t-select-editor';
import { TextEditorTagName, createTextEditor } from './components/t-text-editor';
import { TextareaEditorTagName, createTextareaEditor } from './components/t-textarea-editor';

import { requiredValidator } from './validators/required-validator';
import { lengthValidator } from './validators/length-validator';
import { regularValidator } from './validators/regular-validator';
import { compareValidator } from './validators/compare-validator';
import { FieldGroupDefinition } from './types/field-definition';
import { ValidatorFn } from './types/validator-fn';

export class FormBuilder {
    private _registeredEditors: {
        [key: string]: CreateEditorFn
    } = {};

    constructor() {
        this._initDefaultEditors();
    }

    private _initDefaultEditors() {
        this.registerEditor(CalendarEditorTagName, createCalendarEditor);
        this.registerEditor(CheckEditorTagName, createCheckEditor);
        this.registerEditor(DatePickerEditorTagName, createDatePickerEditor);
        this.registerEditor(SelectEditorTagName, createSelectEditor);
        this.registerEditor(TextEditorTagName, createTextEditor);
        this.registerEditor(TextareaEditorTagName, createTextareaEditor);
    }

    private _getDefaultFields(form: IForm): { [key: string]: IEditor } {
        const defaultFields: { [key: string]: IEditor } = {};
        const fieldElements = form.element.querySelectorAll<HTMLElement>('[field-name]');
        fieldElements.forEach( (fieldElement: HTMLElement) => {
            const fieldName = fieldElement.getAttribute('field-name');
            const editorType = fieldElement.getAttribute('editor-type');
            if (!editorType) {
                console.warn(`Cannot create editor, the field:${fieldName} does not have editor.`);
                return;
            }
            const createEditorFunc = this._registeredEditors[editorType];
            if (typeof (createEditorFunc) !== 'function') {
                console.warn(`Cannot create editor, the editor:${editorType} not registered.`);
                return;
            }
            //init validators
            const validators: ValidatorFn[] = [];
            //required
            const requiredMessage = fieldElement.getAttribute('data-val-required');
            if (!isEmptyOrWhiteSpace(requiredMessage)) {
                validators.push(requiredValidator({ message: requiredMessage }));
            }
            //length
            const maxLengthMessage = fieldElement.getAttribute('data-val-maxlength');
            const maxLengthValue = fieldElement.getAttribute('data-val-maxlength-max');
            const minLengthMessage = fieldElement.getAttribute('data-val-minlength');
            const minLengthValue = fieldElement.getAttribute('data-val-minlength-min');
            if (!isEmptyOrWhiteSpace(maxLengthValue) || !isEmptyOrWhiteSpace(minLengthValue)) {
                validators.push(lengthValidator({
                    maxLength: Number(maxLengthValue),
                    maxLengthMessage: maxLengthMessage,
                    minLength: Number(minLengthValue),
                    minLengthMessage: minLengthMessage
                }));
            }
            //regular
            const regularMessage = fieldElement.getAttribute('data-val-regex');
            const regularPattern = fieldElement.getAttribute('data-val-regex-pattern');
            if (!isEmptyOrWhiteSpace(regularPattern)) {
                validators.push(regularValidator({
                    message: regularMessage,
                    regExp: new RegExp(regularPattern!)
                }));
            }
            //compare
            const compareMessage = fieldElement.getAttribute('data-val-equalto');
            const compareField = fieldElement.getAttribute('data-val-equalto-other');
            if (!isEmptyOrWhiteSpace(compareField)) {
                const compareFieldName = compareField!.replace('*', this._getFieldPrefix(fieldName!));
                validators.push(compareValidator({
                    compareField: compareFieldName,
                    message: compareMessage
                }));
            }

            const editor = createEditorFunc(form, fieldName!, fieldElement, validators,[]);
            defaultFields[fieldName!] = editor;
        });

        return defaultFields;
    }

    private _getFieldPrefix(fieldName: string) {
        const lastIndexOfDot = fieldName.lastIndexOf('.');
        if (lastIndexOfDot > 0) {
            const prefix = fieldName.substring(0, lastIndexOfDot);
            return prefix;
        }
        return '';
    }


    registerEditor(editorType: string, func: CreateEditorFn):void {
        this._registeredEditors[editorType] = func;
    }

    createForm(formSelector: string, fields?: FieldGroupDefinition): IForm | null {
        //add following line because the Popperjs has the code: if (process.env.NODE_ENV !== "production")
        //todo: there should be a better solution
        const wd= window as any;
        wd.process = { env: { NODE_ENV: 'production' } };

        const formElement: HTMLFormElement | null = document.querySelector(formSelector);
        if (!formElement) {
            console.error(`Cannot create form, the selector ${formSelector} is invalid`);
            return null;
        }

        const form: TForm = new TForm(formElement);

        const formFields = this._getDefaultFields(form);

        if (fields) {
          Object.keys(fields).forEach(fk=> {
            const f = fields[fk];
            let editor = formFields[fk];
                if (!editor) {
                    const editorElement: HTMLElement|null = formElement.querySelector(`[field-name='${fk}']`);
                    if (!editorElement) {
                        console.warn(`Cannot create editor, the field:${fk} not exists.`);
                        return;
                    }
                    const editorType = editorElement.getAttribute('editor-type');
                    if (!editorType) {
                        console.warn(`Cannot create editor, the field:${fk} does not have editor.`);
                        return;
                    }
                    const createEditorFunc = this._registeredEditors[editorType];
                    if (typeof (createEditorFunc) !== 'function') {
                        console.warn(`Cannot create editor, the editor:${editorType} not registered.`);
                        return;
                    }
                    editor = createEditorFunc(form,fk, editorElement, f.validators ?? [], f.asyncValidators??[]);
                    formFields[fk] = editor;
                } else {
                    f.validators?.forEach(v => {
                        editor.validators.push(v);
                    });
                    f.asyncValidators?.forEach(v => {
                        editor.asyncValidators.push(v);
                    });
                }
          });
        }

        const editors: IEditor[] = [];
        Object.keys(formFields).forEach(fk => {
            editors.push(formFields[fk]);
        });

        form.setEditors(editors);

        return form;
    }
}

export const formBuilder = new FormBuilder();
